// Shared route metadata used by both the client-side <RouteSEO> (react-helmet)
// and the build-time prerender plugin (so social crawlers that don't run JS
// still get per-page title / description / og:image).

export const SITE_URL = "https://www.theoctopusmovement.org";

export type Meta = {
  title: string;
  description: string;
  type?: string;
  image?: string;
};

export const META: Record<string, Meta> = {
  "/": {
    title: "The Octopus Movement | 12,000 Minds, One Human Mycelium",
    description:
      "A global nonprofit connecting nonlinear thinkers, different ways of seeing and human intelligence to create possibilities none of us could create alone.",
    image: "/images/og/default.png",
  },
  "/stories": {
    title: "Stories from the Human Mycelium | The Octopus Movement",
    description:
      "Real chains of connection: humans who met through the Human Mycelium and turned a question into something that did not exist before.",
    image: "/images/og/default.png",
  },
  "/who-holds-the-space": {
    title: "Who Holds the Space | The Octopus Movement",
    description:
      "Who founded the movement, who stewards the nonprofit, who holds the rooms, who leads the projects and how decisions are made.",
    image: "/images/og/default.png",
  },
  "/whatsapp": {
    title: "Connect on WhatsApp | The Octopus Movement",
    description:
      "Six open WhatsApp circles inside The Octopus Movement: Conversations, The Spark Jar, Need A Brain?, Ask dr. Octopus Anything, Quick Wins and Podcasts | Books | Movies.",
    image: "/images/og/default.png",
  },
  "/manifesto": {
    title: "Manifesto | The Octopus Movement",
    description:
      "Our manifesto: why nonlinear thinkers are the world's most underused resource, and how we're changing that.",
    type: "article",
    image: "/images/og/manifesto.png",
  },
  "/founding-members": {
    title: "Founding Members | The Octopus Movement",
    description:
      "Meet the founding members of The Octopus Movement, nonlinear thinkers shaping a more human future.",
    image: "/images/og/founding-members.png",
  },
  "/nonlinear-thinkers": {
    title: "Nonlinear Thinkers Through History | The Octopus Movement",
    description:
      "Historical figures whose nonlinear thinking changed the world, from science and art to leadership and activism.",
    image: "/images/og/nonlinear-thinkers.png",
  },
  "/crisis/hunger": {
    title: "The Hunger Crisis | The Octopus Movement",
    description:
      "Facts, stats and stories on global hunger, and how nonlinear thinking can help solve it.",
    image: "/images/og/crisis-hunger.png",
  },
  "/crisis/homelessness": {
    title: "The Homelessness Crisis | The Octopus Movement",
    description: "Understanding global homelessness through a nonlinear lens.",
    image: "/images/og/crisis-homelessness.png",
  },
  "/crisis/climate-change": {
    title: "The Climate Crisis | The Octopus Movement",
    description:
      "Climate change facts, impact and what nonlinear thinkers can do about it.",
    image: "/images/og/crisis-climate-change.png",
  },
  "/crisis/education": {
    title: "The Education Crisis | The Octopus Movement",
    description:
      "Why our education systems are failing nonlinear thinkers, and how to fix them.",
    image: "/images/og/crisis-education.png",
  },
  "/crisis/well-being": {
    title: "The Well-Being Crisis | The Octopus Movement",
    description:
      "The global mental health and well-being crisis through a nonlinear lens.",
    image: "/images/og/crisis-well-being.png",
  },
  "/crisis/leadership": {
    title: "The Leadership Crisis | The Octopus Movement",
    description: "Why the world needs a new kind of nonlinear leadership.",
    image: "/images/og/crisis-leadership.png",
  },
  "/events": {
    title: "Events | The Octopus Movement",
    description: "Upcoming and past events from The Octopus Movement community.",
    image: "/images/og/events.png",
  },
  "/contact": {
    title: "Contact | The Octopus Movement",
    description: "Get in touch with The Octopus Movement.",
    image: "/images/og/contact.png",
  },
  "/tom-explained": {
    title: "tOM Explained | The Octopus Movement",
    description:
      "A technical blueprint of The Octopus Movement, mission, structure, and how it all fits together.",
    type: "article",
    image: "/images/og/tom-explained.png",
  },
  "/why-an-octopus": {
    title: "Why an Octopus? | The Octopus Movement",
    description:
      "The science behind our symbol, why the octopus is the perfect metaphor for nonlinear thinking.",
    type: "article",
    image: "/images/og/why-an-octopus.png",
  },
  "/nonlinear-thinking": {
    title: "Nonlinear Thinking | The Octopus Movement",
    description:
      "The neuroscience and philosophy of nonlinear thinking, including the Japanese concept of Ma.",
    type: "article",
    image: "/images/og/nonlinear-thinking.png",
  },
  "/human-mycelium": {
    title: "The Human Mycelium | The Octopus Movement",
    description:
      "How The Octopus Movement connects nonlinear thinkers worldwide into a living human network.",
    image: "/images/og/human-mycelium.png",
  },
  "/telltom": {
    title: "TellTOM App | The Octopus Movement",
    description:
      "TellTOM turns your frustrations into data that fuels real-world change. Download for iOS and Android.",
    image: "/images/og/telltom.png",
  },
  "/sustainability": {
    title: "Sustainability | The Octopus Movement",
    description: "Our commitments to people, planet and a sustainable future.",
  },
  "/human-rights-policy": {
    title: "Human Rights Policy | The Octopus Movement",
    description: "Our human rights policy and commitments.",
  },
  "/privacy-statement": {
    title: "Privacy Statement | The Octopus Movement",
    description:
      "How The Octopus Movement collects, uses and protects your data.",
  },
  "/think-tank": {
    title: "The Think Tank | The Octopus Movement",
    description:
      "The Octopus Movement Think Tank, diverse, nonlinear minds tackling the world's hardest problems.",
    image: "/images/og/think-tank.png",
  },
  "/donate": {
    title: "Mycelium Supporter | The Octopus Movement",
    description:
      "Support The Octopus Movement and help nonlinear thinkers change the world.",
    image: "/images/og/donate.png",
  },
  "/mycelial-connection": {
    title: "Mycelial Connection | The Octopus Movement",
    description:
      "Find your people. Connect with nonlinear thinkers across the global Octopus network.",
    image: "/images/og/mycelial-connection.png",
  },
  "/kids-council": {
    title: "The Kids Council | The Octopus Movement",
    description:
      "The mind of a child. A global session where children create a white paper on the big questions of humanity.",
    image: "/images/og/kids-council.png",
  },
  "/science-member": {
    title: "Become a Science Member | The Octopus Movement",
    description:
      "Research with the people, not only about the people. A global research companion for scientists studying the complexity of being human.",
    image: "/images/og/default.png",
  },
  "/wisdom-membership": {
    title: "Wisdom Membership | The Octopus Movement",
    description:
      "What are we not seeing? A global space to explore what exists underneath knowledge: monthly wisdom questions, unlearning experiments and collective seeing.",
    image: "/images/og/default.png",
  },
  "/surprise": {
    title: "Surprise | The Octopus Movement",
    description: "An unexpected page from The Octopus Movement.",
  },
  "/wall-of-thoughts": {
    title: "The Wall of Thoughts | The Octopus Movement",
    description:
      "A living wall of research ideas from curious minds worldwide. Share what needs research across neurodiversity, human intelligence, education, AI, leadership, and creativity.",
    image: "/images/og/default.png",
  },
  "/human-score": {
    title: "Our Human Score | The Octopus Movement",
    description:
      "The Octopus Movement holds an A-level Human Score™, an independent, science-based measure of human-centricity. See our live community dashboard.",
    image: "/images/og/default.png",
  },
  "/octopus-anonymous": {
    title: "Octopus Anonymous | The Octopus Movement",
    description:
      "A weekly online circle for humans who want somewhere to simply arrive. No performance, no camera required, no advice. Not therapy, just a room.",
    image: "/images/og/default.png",
  },
  "/shop": {
    title: "Sticker Shop | The Octopus Movement",
    description:
      "Shop The Octopus Movement sticker collection, small declarations for nonlinear minds.",
    image: "/images/og/default.png",
  },
};

export const DEFAULT_META: Meta = META["/"];
export const DEFAULT_IMAGE = "/images/og/default.png";

export function resolveMeta(pathname: string): Meta {
  return META[pathname] ?? DEFAULT_META;
}

export function absoluteUrl(p: string): string {
  return `${SITE_URL}${p}`;
}
